import React, { useState, useEffect, useCallback } from 'react';
import { importAPI } from '../api';
import { toast } from 'sonner';
import { Upload, CheckCircle, AlertTriangle, Trash2, ArrowRight, ArrowLeft, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ENTITY_FIELDS = {
  leads: { label: 'Leads', required: ['customer_name', 'primary_mobile', 'lead_source'], fields: ['customer_name', 'primary_mobile', 'email', 'country', 'state', 'city', 'locality', 'lead_source', 'services_required', 'budget', 'status', 'project_name', 'remarks'] },
  b2b_partners: { label: 'B2B Partners', required: ['company_name', 'mobile'], fields: ['company_name', 'contact_person', 'mobile', 'alternate_mobile', 'email', 'city', 'gstin', 'partner_type'] },
};
const guessTarget = (col, fields) => {
  const c = col.toLowerCase().replace(/[^a-z]/g, '');
  const map = { name: 'customer_name', customername: 'customer_name', client: 'customer_name', company: 'company_name', companyname: 'company_name', phone: 'primary_mobile', mobile: 'mobile', primarymobile: 'primary_mobile', contact: 'contact_person', altmobile: 'alternate_mobile', alternatemobile: 'alternate_mobile', email: 'email', city: 'city', state: 'state', country: 'country', locality: 'locality', area: 'locality', source: 'lead_source', leadsource: 'lead_source', budget: 'budget', status: 'status', gstin: 'gstin', gst: 'gstin', service: 'services_required', services: 'services_required' };
  return fields.includes(map[c]) ? map[c]
    // a "Mobile" column means primary_mobile when the target entity has that field (Leads)
    : (map[c] === 'mobile' && fields.includes('primary_mobile')) ? 'primary_mobile'
      : (fields.includes(c) ? c : '');
};

const HISTORY_RANGES = [['', 'All time'], ['today', 'Today'], ['week', 'This Week'], ['month', 'This Month'], ['custom', 'Custom']];

export const BulkImportPage = () => {
  const { user, hasPermission } = useAuth();
  const canSeeAllHistory = user?.role === 'super_admin' || hasPermission?.('import.history.view_all');
  const [step, setStep] = useState(1);
  const [entity, setEntity] = useState('leads');
  const [file, setFile] = useState(null);
  const [source, setSource] = useState('gsheet');     // 'gsheet' | 'file'
  const [sheetUrl, setSheetUrl] = useState('');
  const [sheet, setSheet] = useState(null);           // {sheet_key, total_rows, already_imported}
  const [sheetSources, setSheetSources] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [useLink, setUseLink] = useState(false);
  const [hist, setHist] = useState({ period: '', date_from: '', date_to: '', sheet_source_id: '', imported_by: '' });
  const [batchLeads, setBatchLeads] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);

  const cfg = ENTITY_FIELDS[entity];

  const loadBatches = useCallback(async () => {
    const params = {};
    if (hist.period && hist.period !== 'custom') params.period = hist.period;
    if (hist.period === 'custom') { if (hist.date_from) params.date_from = hist.date_from; if (hist.date_to) params.date_to = hist.date_to; }
    if (hist.sheet_source_id) params.sheet_source_id = hist.sheet_source_id;
    if (hist.imported_by) params.imported_by = hist.imported_by;
    try { const r = await importAPI.batches(params); setBatches(r.data); } catch (e) {}
  }, [hist]);
  useEffect(() => { loadBatches(); }, [loadBatches]);
  useEffect(() => { importAPI.sheetSources().then((r) => setSheetSources(r.data || [])).catch(() => setSheetSources([])); }, []);

  const importers = Array.from(new Map((batches || []).filter(b => b.imported_by)
    .map(b => [b.imported_by, b.imported_by_name])).entries());

  const openBatchLeads = async (b) => {
    try { const r = await importAPI.batchLeads(b.batch_id); setBatchLeads({ ...r.data, batch: b }); }
    catch (e) { toast.error('Could not load leads for this import'); }
  };

  const doParse = async () => {
    if (!file) { toast.error('Choose a CSV or XLSX file'); return; }
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await importAPI.parse(fd);
      setParsed(r.data);
      const m = {}; (r.data.columns || []).forEach(col => { const t = guessTarget(col, cfg.fields); if (t) m[col] = t; });
      setMapping(m);
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || 'Parse failed'); }
    finally { setLoading(false); }
  };

  const doFetchSheet = async () => {
    if ((useLink || !sheetSources.length) && !sheetUrl.trim()) { toast.error('Paste the Google Sheet link'); return; }
    if (!useLink && sheetSources.length && !sourceId) { toast.error('Select a Google Sheet source'); return; }
    setLoading(true);
    try {
      const r = await importAPI.gsheetFetch(useLink || !sourceId ? { url: sheetUrl.trim() } : { source_id: sourceId });
      const d = r.data;
      setSheet({ sheet_key: d.sheet_key, source_id: d.source_id, source_name: d.source_name,
        access_mode: d.access_mode, total_rows: d.total_rows, already_imported: d.already_imported, new_rows: d.new_rows });
      setParsed({ columns: d.headers, rows: d.rows, total_rows: d.new_rows });
      const m = {}; (d.headers || []).forEach(col => { const t = guessTarget(col, cfg.fields); if (t) m[col] = t; });
      setMapping(m);
      if (!d.new_rows) { toast.info(`No new rows — all ${d.total_rows} rows were already imported`); }
      else { toast.success(`${d.new_rows} new row(s) found (${d.already_imported} already imported)`); setStep(3); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read that Sheet'); }
    finally { setLoading(false); }
  };

  const doPreview = async () => {
    setLoading(true);
    try {
      const r = await importAPI.preview({ entity, rows: parsed.rows, mapping });
      setPreview(r.data); setStep(4);
    } catch (e) { toast.error('Preview failed'); }
    finally { setLoading(false); }
  };

  const doConfirm = async () => {
    setLoading(true);
    try {
      const r = await importAPI.confirm({ entity, rows: parsed.rows, mapping,
        ...(sheet ? { sheet_key: sheet.sheet_key, ...(sheet.source_id ? { sheet_source_id: sheet.source_id } : {}) } : {}) });
      setResult(r.data); setStep(5); loadBatches();
      toast.success(`Imported ${r.data.imported} of ${r.data.total}`);
    } catch (e) { toast.error('Import failed'); }
    finally { setLoading(false); }
  };

  const rollback = async (id) => {
    if (!window.confirm(`Rollback import ${id}? This removes only records from that import.`)) return;
    try { const r = await importAPI.rollback(id); toast.success(`Removed ${r.data.removed} records`); loadBatches(); }
    catch (e) { toast.error('Rollback failed'); }
  };

  const reset = () => { setStep(1); setFile(null); setParsed(null); setMapping({}); setPreview(null); setResult(null); setSheet(null); setSheetUrl(''); };

  const steps = ['Entity', 'Upload', 'Map Columns', 'Preview & Validate', 'Summary'];

  return (
    <div className="space-y-6" data-testid="bulk-import-page">
      <div>
        <h1 className="text-2xl font-outfit font-semibold flex items-center gap-2"><Upload className="h-6 w-6 text-primary" /> Bulk Data Import</h1>
        <p className="text-sm text-muted-foreground">Safely import Leads & B2B Partners from CSV or Excel. Duplicate rules are enforced.</p>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2">
        {steps.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs ${step === i + 1 ? 'bg-primary text-white' : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-slate-100 dark:bg-slate-800 text-muted-foreground'}`}>
            {step > i + 1 ? <CheckCircle className="h-3 w-3" /> : <span>{i + 1}</span>}{s}
          </div>
        ))}
      </div>

      <div className="clay-card p-6">
        {step === 1 && (
          <div className="space-y-4" data-testid="import-step-entity">
            <h3 className="font-medium">1. Select what to import</h3>
            <div className="flex gap-3">
              {Object.entries(ENTITY_FIELDS).map(([k, v]) => (
                <button key={k} onClick={() => setEntity(k)} data-testid={`import-entity-${k}`} className={`px-4 py-3 rounded-lg border ${entity === k ? 'border-primary bg-primary/5' : 'border-slate-200 dark:border-slate-700'}`}>{v.label}</button>
              ))}
            </div>
            <button onClick={() => setStep(2)} data-testid="import-next-1" className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm">Next <ArrowRight className="h-4 w-4" /></button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="import-step-upload">
            <h3 className="font-medium">2. Choose the source</h3>
            <div className="flex flex-wrap gap-2">
              {[['gsheet', 'Google Sheet'], ['file', 'CSV / Excel file']].map(([k, label]) => (
                <button key={k} onClick={() => setSource(k)} data-testid={`import-source-${k}`}
                  className={`rounded-lg border px-3 py-1.5 text-sm ${source === k ? 'border-primary bg-primary/10 font-medium text-primary' : 'border-slate-200 dark:border-slate-700'}`}>{label}</button>
              ))}
            </div>
            {source === 'file' ? (
              <>
                <input type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files[0])} data-testid="import-file-input" className="block text-sm" />
                <p className="text-xs text-muted-foreground">Required columns for {cfg.label}: {cfg.required.join(', ')}.</p>
              </>
            ) : (
              <>
                {sheetSources.length > 0 && !useLink ? (
                  <>
                    <label className="block text-xs text-muted-foreground">Google Sheet source</label>
                    <select value={sourceId} onChange={e => setSourceId(e.target.value)} data-testid="import-sheet-source"
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
                      <option value="">Select a configured Sheet…</option>
                      {sheetSources.map(s => <option key={s.id} value={s.id}>{s.name}{s.tab_range ? ` · ${s.tab_range}` : ''}</option>)}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      Each Sheet stays <strong>Restricted</strong> — GNONS reads it with read-only service-account access.
                      Only rows you have not imported before are fetched.
                    </p>
                    <button type="button" onClick={() => setUseLink(true)} data-testid="import-sheet-use-link"
                      className="text-xs text-primary underline">Use a shared link instead</button>
                  </>
                ) : (
                  <>
                    <input type="url" value={sheetUrl} onChange={e => setSheetUrl(e.target.value)} data-testid="import-sheet-url"
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
                    <p className="text-xs text-muted-foreground">
                      Paste the Sheet link. A <strong>Restricted</strong> Sheet works as long as it is shared as
                      <strong>Viewer</strong> with the GNONS service account (see Settings → Google Sheet Sources);
                      “Anyone with the link” is only needed as a fallback. The first row must be the column headings.
                    </p>
                    {sheetSources.length > 0 && (
                      <button type="button" onClick={() => setUseLink(false)} data-testid="import-sheet-use-source"
                        className="text-xs text-primary underline">Choose a configured Sheet source instead</button>
                    )}
                  </>
                )}
                {sheet && (
                  <p className="text-xs text-sky-600" data-testid="import-sheet-summary">
                    {sheet.source_name ? `${sheet.source_name} · ` : ''}{sheet.total_rows} row(s) in the Sheet · {sheet.new_rows} new · {sheet.already_imported} already imported{sheet.access_mode === 'service_account' ? ' · private access' : ''}
                  </p>
                )}
              </>
            )}
            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm"><ArrowLeft className="h-4 w-4" /> Back</button>
              {source === 'file' ? (
                <button onClick={doParse} disabled={loading} data-testid="import-parse" className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{loading ? 'Parsing…' : 'Parse File'}</button>
              ) : (
                <button onClick={doFetchSheet} disabled={loading} data-testid="import-sheet-fetch" className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{loading ? 'Fetching…' : 'Fetch / Refresh Sheet'}</button>
              )}
            </div>
          </div>
        )}

        {step === 3 && parsed && (
          <div className="space-y-4" data-testid="import-step-mapping">
            <h3 className="font-medium">3. Map columns ({parsed.total_rows} rows)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {parsed.columns.map(col => (
                <div key={col} className="flex items-center gap-2 text-sm">
                  <span className="w-1/2 truncate font-medium">{col}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <select value={mapping[col] || ''} onChange={e => setMapping(m => ({ ...m, [col]: e.target.value }))} data-testid={`import-map-${col}`} className="w-1/2 px-2 py-1 border rounded-lg bg-white dark:bg-slate-900">
                    <option value="">— Ignore —</option>
                    {cfg.fields.map(f => <option key={f} value={f}>{f}{cfg.required.includes(f) ? ' *' : ''}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(2)} className="inline-flex items-center gap-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button onClick={doPreview} disabled={loading} data-testid="import-preview" className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{loading ? 'Validating…' : 'Preview & Validate'}</button>
            </div>
          </div>
        )}

        {step === 4 && preview && (
          <div className="space-y-4" data-testid="import-step-preview">
            <h3 className="font-medium">4. Preview & Validation</h3>
            <div className={`grid grid-cols-2 gap-3 ${sheet ? 'sm:grid-cols-6' : 'sm:grid-cols-5'}`}>
              {[['Total', preview.total, ''], ['Valid', preview.valid, 'text-green-600'], ['Invalid', preview.invalid, 'text-red-600'], ['Duplicates', preview.duplicates, 'text-amber-600'], ['Recycle Bin', preview.recycle_bin_matches || 0, 'text-sky-600'],
                ...(sheet ? [['Already Imported', sheet.already_imported, 'text-muted-foreground']] : [])].map(([l, v, c]) => (
                <div key={l} className="clay-card p-3" data-testid={`import-count-${l.toLowerCase()}`}><div className="text-xs text-muted-foreground">{l}</div><div className={`text-xl font-semibold ${c}`}>{v}</div></div>
              ))}
            </div>
            <div className="max-h-72 overflow-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground sticky top-0 bg-white dark:bg-slate-900"><tr><th className="py-1 px-2">Row</th><th>Data</th><th>Status</th></tr></thead>
                <tbody>
                  {preview.rows.slice(0, 200).map((r) => (
                    <tr key={r.row} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1 px-2">{r.row}</td>
                      <td className="max-w-md truncate">{Object.entries(r.data).filter(([, v]) => v).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join('/') : v}`).join(', ')}</td>
                      <td data-testid={`import-row-status-${r.row}`}>
                        {r.errors.length
                          ? <span className={`flex items-center gap-1 ${r.status === 'active_duplicate' || r.status === 'duplicate_in_file' ? 'text-amber-600' : 'text-red-600'}`}><AlertTriangle className="h-3 w-3" />{r.errors.join('; ')}</span>
                          : r.recycle_bin_match
                            ? <span className="flex items-start gap-1 text-sky-600"><Trash2 className="mt-0.5 h-3 w-3 shrink-0" />
                                <span>Match in Recycle Bin{r.recycle_bin_match.lead_no ? ` (${r.recycle_bin_match.lead_no})` : ''} — no active lead, will import as new</span>
                              </span>
                            : <span className="text-green-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Valid</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setStep(3)} className="inline-flex items-center gap-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm"><ArrowLeft className="h-4 w-4" /> Back</button>
              <button onClick={doConfirm} disabled={loading || preview.valid === 0} data-testid="import-confirm" className="inline-flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50">{loading ? 'Importing…' : `Import ${preview.valid} Valid Rows`}</button>
            </div>
          </div>
        )}

        {step === 5 && result && (
          <div className="space-y-4 text-center" data-testid="import-step-summary">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <h3 className="font-medium text-lg">Import Complete</h3>
            <p className="text-sm text-muted-foreground">Batch <span className="font-mono">{result.batch_id}</span></p>
            <div className="grid grid-cols-4 gap-3 max-w-lg mx-auto">
              {[['Total', result.total], ['Imported', result.imported], ['Skipped', result.skipped], ['Failed', result.failed]].map(([l, v]) => (
                <div key={l} className="clay-card p-3"><div className="text-xs text-muted-foreground">{l}</div><div className="text-xl font-semibold">{v}</div></div>
              ))}
            </div>
            <button onClick={reset} data-testid="import-restart" className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Import Another File</button>
          </div>
        )}
      </div>

      {/* Import record */}
      <div className="clay-card p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">Import Record</h3>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {HISTORY_RANGES.map(([k, label]) => (
              <button key={k || 'all'} onClick={() => setHist(h => ({ ...h, period: k }))} data-testid={`import-hist-range-${k || 'all'}`}
                className={`rounded-full px-2.5 py-1 ${hist.period === k ? 'bg-primary text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>{label}</button>
            ))}
            {hist.period === 'custom' && (
              <>
                <input type="date" value={hist.date_from} onChange={e => setHist(h => ({ ...h, date_from: e.target.value }))}
                  data-testid="import-hist-from" className="rounded-lg border px-2 py-1 dark:bg-slate-900" />
                <input type="date" value={hist.date_to} onChange={e => setHist(h => ({ ...h, date_to: e.target.value }))}
                  data-testid="import-hist-to" className="rounded-lg border px-2 py-1 dark:bg-slate-900" />
              </>
            )}
            <select value={hist.sheet_source_id} onChange={e => setHist(h => ({ ...h, sheet_source_id: e.target.value }))}
              data-testid="import-hist-source" className="rounded-lg border px-2 py-1 dark:bg-slate-900">
              <option value="">All Sheet sources</option>
              {sheetSources.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {canSeeAllHistory && (
              <select value={hist.imported_by} onChange={e => setHist(h => ({ ...h, imported_by: e.target.value }))}
                data-testid="import-hist-user" className="rounded-lg border px-2 py-1 dark:bg-slate-900">
                <option value="">All users</option>
                {importers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
            )}
          </div>
        </div>
        {batches.length === 0 ? <p className="text-sm text-muted-foreground">No imports for this selection.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground"><tr>
                <th className="py-2">Batch</th><th>Source</th><th>Entity</th><th>Processed</th><th>Imported</th>
                <th>Skipped</th><th>Failed</th><th>By</th><th>Date</th><th></th>
              </tr></thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.batch_id} className="border-t border-slate-100 dark:border-slate-800" data-testid="import-batch-row">
                    <td className="py-2 font-mono text-xs">{b.batch_id}</td>
                    <td className="text-xs">{b.sheet_source_name || (b.import_source === 'gsheet' ? 'Google Sheet (link)' : 'File upload')}
                      {b.sheet_tab ? <span className="text-muted-foreground"> · {b.sheet_tab}</span> : null}</td>
                    <td>{b.entity}</td><td>{b.total ?? '—'}</td>
                    <td><button onClick={() => openBatchLeads(b)} data-testid={`import-batch-leads-${b.batch_id}`}
                      className="text-primary underline">{b.imported}</button></td>
                    <td>{b.skipped}</td><td>{b.failed}</td><td className="text-xs">{b.imported_by_name}</td>
                    <td className="text-xs">{b.imported_at ? new Date(b.imported_at).toLocaleString() : '—'}</td>
                    <td><button onClick={() => rollback(b.batch_id)} data-testid="import-rollback" className="text-red-500 inline-flex items-center gap-1 text-xs"><Trash2 className="h-3 w-3" /> Rollback</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {batchLeads && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4" onClick={() => setBatchLeads(null)}>
          <div className="mt-10 w-full max-w-3xl rounded-xl bg-white p-4 dark:bg-slate-900" onClick={e => e.stopPropagation()} data-testid="import-batch-leads-modal">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="font-medium">Leads from {batchLeads.batch?.sheet_source_name || 'this import'}</h3>
                <p className="text-xs text-muted-foreground font-mono">{batchLeads.batch_id} · {batchLeads.total} lead(s) visible to you</p>
              </div>
              <button onClick={() => setBatchLeads(null)} data-testid="import-batch-leads-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-80 overflow-auto rounded-lg border dark:border-slate-700">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-white text-left text-muted-foreground dark:bg-slate-900">
                  <tr><th className="px-2 py-1">Lead No</th><th>Name</th><th>Mobile</th><th>City</th><th>Status</th><th>Created</th></tr>
                </thead>
                <tbody>
                  {batchLeads.items.map(l => (
                    <tr key={l.id} className="border-t border-slate-100 dark:border-slate-800" data-testid="import-batch-lead-row">
                      <td className="px-2 py-1 font-mono">{l.lead_no || '—'}</td><td>{l.customer_name}</td><td>{l.primary_mobile}</td>
                      <td>{l.city || '—'}</td><td>{l.status}</td><td>{l.created_at ? new Date(l.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                  {batchLeads.items.length === 0 && <tr><td colSpan={6} className="px-2 py-2 text-muted-foreground">No leads from this import are within your access scope.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
export default BulkImportPage;
